/**
 * BIP (Boda Intelligent Prompt) launcher.
 *
 * Shared by UILayout (multi-extension) and UILayoutExtension (standalone) so that
 * any extension — or the status bar — can open a BIP chat with a context via:
 *
 *   await window['bodapro-context'].executeCommand('bodapro.bip.open', {
 *       contextKey: 'bss.esomelo',          // optional: context to pre-select
 *       title: 'Esomelo Assistant',         // optional: chat panel title
 *       prompt: 'list unpaid invoices'      // optional: prefill the input box
 *   });
 *
 * The launcher enables (installs) the bip extension first if the user does not
 * have it yet — same flow as the "Enable AI Assistant" button on the welcome
 * panel — then lazy-loads it via bodapro.extensions.activate.
 */

export const BIP_EXTENSION_NAME = 'bip';
export const BIP_OPEN_COMMAND = 'bodapro.bip.open';
export const BIP_SHOW_NEW_CHAT_COMMAND = 'bss.bip:show-new-chat';

// BIP is actually loaded only once its commands are registered — BODAPRO_EXTENSIONS
// alone is not enough (bodapro.extensions.install adds the name without activating)
const isBipReady = () => globalThis['commands$']?.[BIP_SHOW_NEW_CHAT_COMMAND] !== undefined;

const isBipInstalled = () => (globalThis['BODAPRO_EXTENSIONS'] || []).indexOf(BIP_EXTENSION_NAME) > -1;

const hasExecutableCommand = (name) => globalThis.executableCommands$?.[name] !== undefined;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Register the bodapro.bip.open executable command on window['bodapro-context'].
 * Safe to call on every render — registerExecutableCommand ignores duplicates.
 */
export function registerBipLauncher() {
    const ctx = window['bodapro-context'];
    if (!ctx) return;

    ctx.registerExecutableCommand(BIP_OPEN_COMMAND, async function ({ contextKey, title, prompt } = {}) {

        if (!isBipReady()) {
            try {
                // Enable the extension for the user first if needed (same as the
                // welcome panel button). Not registered in standalone mode, where
                // extensions load straight from the assets server.
                if (!isBipInstalled() && hasExecutableCommand('bodapro.extensions.install')) {
                    ctx.showToast({ message: 'Enabling AI Assistant...', intent: 'primary' });
                    await ctx.executeCommand('bodapro.extensions.install', { name: BIP_EXTENSION_NAME });
                }

                // Lazy-load the bip extension
                await ctx.executeCommand('bodapro.extensions.activate', { name: BIP_EXTENSION_NAME });
            } catch (error) {
                console.error('Failed to enable/activate BIP extension:', error);
                ctx.showToast({ message: 'BIP assistant is not available', intent: 'danger' });
                return { success: false, error: error?.message || String(error) };
            }

            // Defensive check: BIP's activate() swallows errors, so verify the command exists
            if (!isBipReady()) {
                await wait(100);
                if (!isBipReady()) {
                    ctx.showToast({ message: 'BIP assistant is not available', intent: 'danger' });
                    return { success: false, error: 'BIP did not register its commands' };
                }
            }
        }

        // BIP chat panels open in the secondary sidebar; make sure it is visible
        // (standalone layouts may have constrained it to width 0)
        ctx.showSection('secondary-sidebar');

        ctx.triggerCommand(BIP_SHOW_NEW_CHAT_COMMAND, { title, contextKey, prompt });

        return { success: true, contextKey };
    });
}

/**
 * Background lazy-load of the bip extension (standalone mode). Fire-and-forget:
 * never throws and never blocks host extension startup. Activation only — it
 * does not install/enable the extension for the user.
 */
export function preloadBip({ delayMs = 2500 } = {}) {
    if (globalThis['__bipPreloadStarted']) return;
    globalThis['__bipPreloadStarted'] = true;

    setTimeout(() => {
        if (isBipReady()) return;
        const ctx = window['bodapro-context'];
        if (!ctx) return;
        ctx.executeCommand('bodapro.extensions.activate', { name: BIP_EXTENSION_NAME })
            .catch((e) => console.warn('BIP preload failed (non-fatal):', e));
    }, delayMs);
}
